import {
  ContactFieldSyncDetailConfig,
  MappableSalesforceField,
  SalesforceSyncTypes,
} from 'module/api/integrations/salesforce/types';
import SalesforceLogo from 'module/ui/assets/svgs/salesforce-logo.svg';
import { Modal } from 'module/ui/components/atoms/modal/modal';
import { showToast, ToastType } from 'module/ui/components/molecules/toasts';
import { faSalesforce } from '@fortawesome/free-brands-svg-icons';
import {
  faCircleInfo,
  faLocationArrow,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Loader } from '@rocketlane/ui';
import { Space, Typography, Spin, Avatar } from 'antd';
import {
  useSalesForceMappableFieldsQuery,
  useSaveContactFieldSyncMutation,
} from 'queries/salesforce';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import Select from 'react-select';
import styled from 'styled-components';
import { PopoverSelect } from './resource-mappings/commons/popover-select';
import { RocketlaneFieldValue } from './resource-mappings/commons/rocketlane-field-value';
import {
  SyncOptionLabel,
  SelectedSyncValue,
  SyncSelectStyles,
} from './resource-mappings/commons/sync-utils';
import { syncOptions } from './resource-mappings/use-field-mapping-actions';

const { Text } = Typography;
// Styled components following the repo pattern
const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-04);
`;

const FieldSyncHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-04) var(--spacing-02) var(--spacing-03)
    var(--spacing-07);
  margin-top: var(--spacing-02);
  margin-left: var(--spacing-02);
`;

const ObjectNameRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-03) 0px var(--spacing-03) var(--spacing-08);
  background-color: var(--gray-20);
  border-radius: var(--border-radius-default);
`;

const FieldMappingRow = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--divider-subtle);
  padding: 0px var(--spacing-04) 0px var(--spacing-07);

  &:last-child {
    border-bottom: none;
  }
`;

const FlexOne = styled.div`
  flex: 1;
`;

const SyncDirectionContainer = styled.div`
  width: 32px;
  justify-content: center;
  align-items: center;
  display: flex;
  margin-right: var(--spacing-03);
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: var(--spacing-08) 0;
`;

const LoadingTextContainer = styled.div`
  margin-top: var(--height-xs);
`;

const SpacerDiv = styled.div`
  width: var(--height-base);
  margin-right: var(--spacing-03);
`;

const CenteredText = styled(Text)`
  text-align: center;
  padding: var(--spacing-08) 0;
`;

interface FieldMapping {
  salesforceField?: string | null;
  rocketlaneField?: string | null;
  nativeFieldName?: string;
}

interface ContactFieldSyncModalProps {
  visible: boolean;
  onCancel: () => void;
  onSyncField: () => void; // Simplified - will handle internally
  contactObjectName?: string;
  integrationId: number;
  isLoading?: boolean;
  fieldMappings?: FieldMapping[];
  fieldSyncDetailConfig?: ContactFieldSyncDetailConfig; // Add this to get resourceMappingConfigId
}

// Individual field row component that fetches its own options
interface FieldMappingRowComponentProps {
  integrationId: number;
  contactObjectName: string;
  mapping: FieldMapping;
  index: number;
  selectedField?: MappableSalesforceField; // Changed to store full field object
  syncType?: SalesforceSyncTypes; // Current sync direction
  onFieldSelect: (
    nativeFieldName: string,
    selectedField: MappableSalesforceField,
  ) => void;
  onSyncTypeChange: (
    nativeFieldName: string,
    syncType: SalesforceSyncTypes,
  ) => void;
  parentVisible?: boolean;
}

const FieldMappingRowComponent = ({
  integrationId,
  contactObjectName,
  mapping,
  index,
  selectedField,
  syncType,
  onFieldSelect,
  onSyncTypeChange,
  parentVisible,
}: FieldMappingRowComponentProps) => {
  // Fetch mappable Salesforce fields for this specific Rocketlane field
  const { data: mappableFields, isLoading } = useSalesForceMappableFieldsQuery(
    integrationId,
    contactObjectName,
    'user', // nativeResourceType for contacts
    mapping.nativeFieldName || '',
    {
      enabled: !!mapping.nativeFieldName && !!contactObjectName,
    },
  );

  const hasExistingMapping =
    mapping.salesforceField && mapping.salesforceField !== '-';

  // Convert to select options
  const options = useMemo(() => {
    if (!mappableFields) {
      return [];
    }

    return mappableFields.map((field) => ({
      label: field.fieldLabel,
      value: field.integrationFieldId,
    }));
  }, [mappableFields]);

  // Calculate available sync options based on editableByFieldMapping
  // Option B: Arrow editable only when editableByFieldMapping === true
  const availableSyncOptions = useMemo(() => {
    if (!selectedField) {
      // Default: only Salesforce → Rocketlane (when no field selected yet)
      return [
        {
          label: SalesforceSyncTypes.SyncExternalToNative,
          value: SalesforceSyncTypes.SyncExternalToNative,
        },
      ];
    }

    // If Rocketlane field is NOT editable, only allow SF → RL (arrow NOT editable)
    if (selectedField.editableByFieldMapping === false) {
      return [
        {
          label: SalesforceSyncTypes.SyncExternalToNative,
          value: SalesforceSyncTypes.SyncExternalToNative,
        },
      ];
    }

    // If field IS editable, allow TWO sync directions (arrow IS editable)
    return syncOptions.filter(
      (option) => option.value !== SalesforceSyncTypes.SyncNativeToExternal,
    );
  }, [selectedField]);

  const handleSyncTypeChange = (selected: any) => {
    if (mapping.nativeFieldName && selected?.value) {
      onSyncTypeChange(
        mapping.nativeFieldName,
        selected.value as SalesforceSyncTypes,
      );
    }
  };

  return (
    <FieldMappingRow key={index}>
      {/* Column 1: Salesforce Field */}
      <FlexOne>
        {isLoading ? (
          <Space>
            <FontAwesomeIcon icon={faSalesforce} color="var(--icon-primary)" />
            <Spin size="small" />
          </Space>
        ) : hasExistingMapping ? (
          <PopoverSelect
            loading={false}
            onChange={(selected) => {
              if (selected && mapping.nativeFieldName && mappableFields) {
                const selectedFieldObj = mappableFields.find(
                  (field) => field.integrationFieldId === selected.value,
                );

                if (selectedFieldObj) {
                  onFieldSelect(mapping.nativeFieldName, selectedFieldObj);
                }
              }
            }}
            value={
              selectedField
                ? {
                  value: selectedField?.integrationFieldId || '',
                  label: selectedField?.fieldLabel || '',
                }
                : undefined
            }
            options={options.map((opt) => ({
              value: opt.value,
              label: opt.label || '',
            }))}
            type="salesforce"
            fieldObject={
              {
                externalResourceType: contactObjectName,
              } as any
            }
            zIndex={1200}
            parentVisible={parentVisible}
          />
        ) : (
          <PopoverSelect
            loading={false}
            onChange={(selected) => {
              if (selected && mapping.nativeFieldName && mappableFields) {
                const selectedFieldObj = mappableFields.find(
                  (field) => field.integrationFieldId === selected.value,
                );

                if (selectedFieldObj) {
                  onFieldSelect(mapping.nativeFieldName, selectedFieldObj);
                }
              }
            }}
            value={
              selectedField
                ? {
                  value: selectedField?.integrationFieldId || '',
                  label: selectedField?.fieldLabel || '',
                }
                : undefined
            }
            parentVisible={parentVisible}
            options={options.map((opt) => ({
              value: opt.value,
              label: opt.label || '',
            }))}
            type="salesforce"
            fieldObject={
              {
                externalResourceType: contactObjectName,
              } as any
            }
            zIndex={1200}
          />
        )}
      </FlexOne>

      {/* Column 2: Sync Direction Arrow */}
      <SyncDirectionContainer>
        {(hasExistingMapping || selectedField) && (
          <Select
            isSearchable={false}
            styles={{
              ...SyncSelectStyles,
              control: (provided) => ({
                ...provided,
                boxShadow: 'none',
                border: 'none',
                minHeight: 'auto',
                height: '16px',
                width: '16px',
                backgroundColor: 'transparent',
              }),
              valueContainer: (provided) => ({
                ...provided,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }),
              singleValue: (provided) => ({
                ...provided,
                margin: 0,
                position: 'static',
                transform: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }),
              menuPortal: (provided) => ({
                ...provided,
                zIndex: 1200, // ✅ Added z-index for dropdown menu
              }),
            }}
            onChange={handleSyncTypeChange}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            formatOptionLabel={SyncOptionLabel}
            options={availableSyncOptions}
            isDisabled={availableSyncOptions.length <= 1} // ✅ Disabled when only 1 option (editableByFieldMapping === false)
            value={syncOptions.find((option) => option.value === syncType)}
            defaultValue={syncOptions.find(
              (option) => option.value === syncType,
            )}
            components={{
              DropdownIndicator: () => null,
              IndicatorsContainer: () => null,
              IndicatorSeparator: () => null,
              SingleValue: SelectedSyncValue as any,
            }}
          />
        )}
      </SyncDirectionContainer>

      {/* Column 3: Rocketlane Field */}
      <FlexOne>
        {mapping.rocketlaneField ? (
          <RocketlaneFieldValue label={mapping.rocketlaneField || ''} />
        ) : (
          <Space>
            <FontAwesomeIcon icon={faLocationArrow} color={'var(--blue-50)'} />
            <Text type="secondary">-</Text>
          </Space>
        )}
      </FlexOne>
    </FieldMappingRow>
  );
};

export const ContactFieldSyncModal = ({
  visible,
  onCancel,
  onSyncField,
  contactObjectName = 'Contact',
  integrationId,
  isLoading = false,
  fieldMappings = [],
  fieldSyncDetailConfig,
}: ContactFieldSyncModalProps) => {
  // Track user selections for each field (store full field objects)
  const [fieldSelections, setFieldSelections] = useState<
    Record<string, MappableSalesforceField>
  >({});

  // Track sync type selections for each field
  const [syncTypeSelections, setSyncTypeSelections] = useState<
    Record<string, SalesforceSyncTypes>
  >({});
  const [isSyncButtonLoading, setIsSyncButtonLoading] = useState(false);

  const { mutate: saveFieldSync, isPending: isMutationPending } =
    useSaveContactFieldSyncMutation();
  const isSaving = isSyncButtonLoading || isMutationPending;

  // Reset selections when modal closes
  useEffect(() => {
    if (!visible) {
      setFieldSelections({});
      setSyncTypeSelections({});
      setIsSyncButtonLoading(false);
    }
  }, [visible]);

  // Initialize both field selections and sync types from existing mappings when modal opens
  useEffect(() => {
    if (visible && fieldSyncDetailConfig?.fieldMappings) {
      const initialSyncTypes: Record<string, SalesforceSyncTypes> = {};
      const initialFieldSelections: Record<string, MappableSalesforceField> =
        {};

      fieldSyncDetailConfig.fieldMappings.forEach((mapping) => {
        if (mapping.nativeFieldName && mapping.syncType) {
          initialSyncTypes[mapping.nativeFieldName] =
            mapping.syncType as SalesforceSyncTypes;
        }

        // Initialize field selections from existing mappings
        if (mapping.nativeFieldName && mapping.externalFieldId) {
          // ✅ Infer editableByFieldMapping from allowedSyncTypes
          const editableByFieldMapping =
            mapping.allowedSyncTypes?.includes(
              SalesforceSyncTypes.SyncTwoWay,
            ) || false;

          initialFieldSelections[mapping.nativeFieldName] = {
            integrationFieldId: mapping.externalFieldId,
            fieldLabel: mapping.externalFieldLabel || '',
            fieldName: mapping.externalFieldName || '',
            fieldType: mapping.fieldType || '',
            fieldDataType: mapping.fieldDataType || '',
            isEditable: true,
            isCreatable: true,
            isUpdateable: true,
            isReference: false,
            referenceTo: [],
            isNillable: true,
            isCalculated: false,
            isFilterable: true,
            isSortable: true,
            isUnique: false,
            isExternalId: false,
            isCaseSensitive: false,
            defaultValue: null,
            maxLength: null,
            precision: null,
            scale: null,
            options: [],
            editableByFieldMapping, // ✅ Use inferred value instead of hardcoded true
          } as MappableSalesforceField;
        }
      });

      setSyncTypeSelections(initialSyncTypes);
      setFieldSelections(initialFieldSelections);
    } else if (visible && !fieldSyncDetailConfig?.fieldMappings) {
      // ✅ If modal opens with no existing mappings, ensure clean state
      setFieldSelections({});
      setSyncTypeSelections({});
    }
  }, [visible, fieldSyncDetailConfig]);

  const handleFieldSelect = (
    nativeFieldName: string,
    selectedField: MappableSalesforceField,
  ) => {
    setFieldSelections((prev) => ({
      ...prev,
      [nativeFieldName]: selectedField,
    }));

    // Set default sync type if not already set
    if (!syncTypeSelections[nativeFieldName]) {
      setSyncTypeSelections((prev) => ({
        ...prev,
        [nativeFieldName]: SalesforceSyncTypes.SyncExternalToNative,
      }));
    }
  };

  const handleSyncTypeChange = (
    nativeFieldName: string,
    syncType: SalesforceSyncTypes,
  ) => {
    setSyncTypeSelections((prev) => ({
      ...prev,
      [nativeFieldName]: syncType,
    }));
  };

  const handleSyncClick = useCallback(() => {
    if (isSaving) {
      return;
    }
    setIsSyncButtonLoading(true);
    window.setTimeout(() => {
      // Build the payload according to the API spec
      const payload = {
        resourceMappingConfigId:
          fieldSyncDetailConfig?.resourceMappingConfigId || null,
        integrationId,
        externalIntegrationType: 'salesforce',
        nativeResourceType: 'user',
        externalResourceType: contactObjectName,
        syncType: fieldSyncDetailConfig?.syncType || 'NONE', // Use existing or default
        fieldMappings: fieldMappings.map((mapping) => {
          const selectedField = fieldSelections[mapping.nativeFieldName || ''];
          const existingMapping = fieldSyncDetailConfig?.fieldMappings?.find(
            (fm) => fm.nativeFieldName === mapping.nativeFieldName,
          );

          // Get selected sync type or use existing or default
          const fieldSyncType =
            syncTypeSelections[mapping.nativeFieldName || ''] ||
            existingMapping?.syncType ||
            SalesforceSyncTypes.SyncExternalToNative;

          // Determine allowed sync types based on field editability
          // Only two options: → (SF→RL) and ⇄ (Two-way), no ← (RL→SF)
          const allowedSyncTypes =
            selectedField?.editableByFieldMapping === false
              ? [SalesforceSyncTypes.SyncExternalToNative]
              : [
                SalesforceSyncTypes.SyncExternalToNative,
                SalesforceSyncTypes.SyncTwoWay,
              ];

          return {
            fieldMappingConfigId: existingMapping?.fieldMappingConfigId || null,
            integrationId,
            externalIntegrationType: 'salesforce',
            resouceMappingConfigId:
              fieldSyncDetailConfig?.resourceMappingConfigId || null, // Note: API has typo
            nativeFieldName: mapping.nativeFieldName || '',
            nativeFieldLabel: mapping.rocketlaneField || '',
            externalFieldId:
              selectedField?.integrationFieldId ||
              existingMapping?.externalFieldId ||
              '',
            externalFieldName:
              selectedField?.fieldName ||
              existingMapping?.externalFieldName ||
              '',
            syncType: fieldSyncType,
            allowedSyncTypes,
            externalChildResourceType: selectedField?.externalChildResourceType,
            externalChildFieldId: selectedField?.externalChildFieldId,
            externalParentFieldId: selectedField?.externalParentFieldId,
            enabled: true,
            deleted: false,
          };
        }),
        enabled: true,
        deleted: false,
      };

      saveFieldSync(
        { integrationId, payload },
        {
          onSuccess: () => {
            showToast({
              toastContainer: { current: document.body },
              content: 'Contact field sync configured successfully',
              variant: ToastType.Success,
            });
            onSyncField(); // Notify parent
            onCancel(); // Close modal
          },
          onError: (error) => {
            setIsSyncButtonLoading(false);
            showToast({
              toastContainer: { current: document.body },
              content: error.message || 'Failed to configure contact field sync',
              variant: ToastType.Error,
            });
          },
        },
      );
    }, 0);
  }, [
    fieldSyncDetailConfig,
    integrationId,
    contactObjectName,
    fieldMappings,
    fieldSelections,
    syncTypeSelections,
    saveFieldSync,
    onSyncField,
    onCancel,
    isSaving,
  ]);

  // Check if all unmapped fields have been selected
  const unmappedFields = fieldMappings.filter(
    (m) => !m.salesforceField || m.salesforceField === '-',
  );
  const allFieldsSelected = unmappedFields.every(
    (field) => field.nativeFieldName && fieldSelections[field.nativeFieldName],
  );

  // Handle Enter key press to trigger Sync Field button
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if Enter key is pressed and modal is visible
      if (event.key === 'Enter' && visible && !isSaving && allFieldsSelected) {
        event.preventDefault();
        handleSyncClick();
      }
    };

    if (visible) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, isSaving, allFieldsSelected, handleSyncClick]);

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      width={600}
      zIndex={1100}
      title={
        <Space size="small">
          <FontAwesomeIcon icon={faCircleInfo} />
          <span>Contact field sync not configured for {contactObjectName}</span>
        </Space>
      }
      footer={
        <Space>
          <Button variant="secondary" onClick={onCancel} disabled={isSaving}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            variant="primary"
            onClick={handleSyncClick}
            loading={isSaving}
            disabled={!allFieldsSelected || isSaving}
          >
            Sync Field
            {isSaving && <Loader />}
          </Button>
        </Space>
      }
    >
      <ModalContent>
        {isLoading ? (
          <LoadingContainer>
            <Spin size="large" />
            <LoadingTextContainer>
              <Text type="secondary">Loading field mappings...</Text>
            </LoadingTextContainer>
          </LoadingContainer>
        ) : (
          <>
            <Text>
              Add the required field mapping to enable contact sync for this
              Salesforce object
            </Text>

            {/* Field Mapping Header with Icons */}
            <FieldSyncHeader>
              <FlexOne>
                <Space>
                  <Avatar src={SalesforceLogo} shape="square" size="small" />
                  <Text strong>Salesforce</Text>
                </Space>
              </FlexOne>
              <SpacerDiv />
              <FlexOne>
                <Space>
                  <FontAwesomeIcon
                    icon={faLocationArrow}
                    color={'var(--blue-50)'}
                  />
                  <Text strong>Rocketlane</Text>
                </Space>
              </FlexOne>
            </FieldSyncHeader>

            {/* Description text */}
            {/* <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 'var(--spacing-02)' }}>
              The object name selected for contact.
            </Text> */}

            {/* Object Name Row */}
            <ObjectNameRow>
              <FlexOne>
                <Space>
                  <Text>{contactObjectName}</Text>
                </Space>
              </FlexOne>
              <SpacerDiv />
              <FlexOne>
                <Space>
                  <Text>People</Text>
                </Space>
              </FlexOne>
            </ObjectNameRow>

            {/* Field Mapping Rows */}
            {fieldMappings.length > 0 ? (
              fieldMappings.map((mapping, index) => (
                <FieldMappingRowComponent
                  key={`${index}-${visible}-${mapping.nativeFieldName}`}
                  integrationId={integrationId}
                  contactObjectName={contactObjectName}
                  mapping={mapping}
                  index={index}
                  selectedField={
                    mapping.nativeFieldName
                      ? fieldSelections[mapping.nativeFieldName]
                      : undefined
                  }
                  syncType={
                    mapping.nativeFieldName
                      ? syncTypeSelections[mapping.nativeFieldName] ||
                      SalesforceSyncTypes.SyncExternalToNative
                      : SalesforceSyncTypes.SyncExternalToNative
                  }
                  onFieldSelect={handleFieldSelect}
                  onSyncTypeChange={handleSyncTypeChange}
                  parentVisible={visible}
                />
              ))
            ) : (
              <CenteredText type="secondary">
                No mandatory fields found
              </CenteredText>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
